import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import _ from "lodash";
import apiClient from "../services/apiClient";
import Comments from "../components/Comments";
import {
  Container,
  Typography,
  Button,
  Paper,
  Box,
  LinearProgress,
} from "@mui/material";
// import ReadAloud from "./ReadAloud";

const readOffline = (chapterId) => {
  const chapterData = localStorage.getItem(`chapter-${chapterId}`);
  if (chapterData) {
    return JSON.parse(chapterData);
  } else {
    alert("No offline data available for this chapter.");
    return null;
  }
};


const ChapterPage = () => {
  const { id: storyId, chapter_id: chapterId } = useParams();
  const [chapter, setChapter] = useState(null);
  const [position, setPosition] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const scrollContainerRef = useRef(null);
  const [isOffline, setIsOffline] = useState(false);
  const containerRef = useRef(null);

  const [fontSize, setFontSize] = useState(() => {
    const savedSize = localStorage.getItem('fontSize');
    return savedSize ? parseInt(savedSize) : 16;
  });
  
  const [fontFamily, setFontFamily] = useState(() => {
    return localStorage.getItem('fontFamily') || 'Arial';
  });

  const increaseFontSize = () => {
    const newSize = Math.min(fontSize + 2, 30);
    setFontSize(newSize);
    localStorage.setItem('fontSize', newSize.toString());
  };

  const decreaseFontSize = () => {
    const newSize = Math.max(fontSize - 2, 12);
    setFontSize(newSize);
    localStorage.setItem('fontSize', newSize.toString());
  };

  const changeFont = (font) => {
    setFontFamily(font);
    localStorage.setItem('fontFamily', font);
  };

  const toggleDarkMode = () => {
    setDarkMode((prev) => !prev);
    localStorage.setItem("darkMode", !darkMode);
  };

  useEffect(() => {
    const savedMode = localStorage.getItem("darkMode") === "true";
    setDarkMode(savedMode);
  }, []);

  const addBookmark = () => {
    apiClient.post("/api/v1/bookmarks", { 
      story_id: storyId,
      chapter_id: chapterId,
      position: position,
    })
      .then(() => alert("Bookmark added!"))
      .catch((error) => console.error("Error adding bookmark:", error));
  };

  useEffect(() => {
    console.log("ChapterPage useEffect", navigator.onLine);
    if (navigator.onLine) {
      // Fetch chapter content
      const fetchChapterUrl = storyId ? `/api/v1/stories/${storyId}/chapters/${chapterId}` : `/api/v1/chapters/${chapterId}`;

      apiClient.get(fetchChapterUrl)
      .then((response) => setChapter(response.data))
      .catch((error) => console.error("Error fetching chapter:", error));

      if (storyId) {
        // Fetch reading progress
        apiClient.get(`/api/v1/progress?story_id=${storyId}`)
          .then((response) => setPosition(response.data?.position || 0))
          .catch((error) => console.error("Error fetching progress:", error));
      }
    } else {
      // Đọc nội dung từ localStorage nếu offline
      const offlineData = readOffline(chapterId);
      if (offlineData) {
        setChapter(offlineData);
        setIsOffline(true);
      }
    }
  }, [storyId, chapterId]);

  const saveOffline = () => {
    apiClient
      .get(`/api/v1/stories/${storyId}/chapters/${chapterId}/download`)
      .then((response) => {
        localStorage.setItem(`chapter-${chapterId}`, JSON.stringify(response.data));
        alert("Chapter saved for offline reading!");
      })
      .catch((error) => console.error("Error saving chapter offline:", error));
  };

  const saveProgress = useCallback(
    _.debounce((currentPosition) => {
      apiClient.patch("/api/v1/progress/update_progress", {
        story_id: storyId,
        chapter_id: chapterId,
        position: currentPosition,
      });
    }, 500), // Call the API only every 500ms
    []
  );

  const handleScroll = useCallback((event) => {
    const element = event.target;
    const currentPosition = Math.floor(
      (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100
    );
    setPosition(currentPosition);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.mozRequestFullScreen) {
        containerRef.current.mozRequestFullScreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  if (!chapter) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Container
      ref={(el) => {
        containerRef.current = el;
        scrollContainerRef.current = el;
      }}
      style={{ 
        overflowY: "auto", 
        height: "100vh", 
        padding: "16px", 
        backgroundColor: darkMode ? '#333' : '#fff', 
        color: darkMode ? '#fff' : '#000',
      }}
    >
      <Box mb={2}>
        <LinearProgress variant="determinate" value={position} />
      </Box>

      <Typography variant="h4" gutterBottom>
        {chapter.title}
      </Typography>

      <Box display="flex" justifyContent="space-between" mb={2}>
        <Box display="flex" gap={1}>
          <Button variant="contained" onClick={toggleFullScreen}>
            {isFullScreen ? "Thoát chế độ toàn màn hình" : "Chế độ toàn màn hình"}
          </Button>
          <Button variant="contained" onClick={addBookmark}>
            Add Bookmark
          </Button>
          {/* <ReadAloud content={chapter.content} /> */}
          {isOffline ? (
            <Typography color="textSecondary">You are reading offline</Typography>
          ) : (
            <Button variant="contained" onClick={saveOffline}>
              Save Offline
            </Button>
          )}
        </Box>
        <Typography>{position}% đã đọc</Typography>
      </Box>

      <Box mt={2}>
        <Button variant="contained" onClick={toggleDarkMode}>
          {darkMode ? "Disable Dark Mode" : "Enable Dark Mode"}
        </Button>

        <Box display="flex" alignItems="center" gap={2} mt={2}>
          <Button variant="contained" onClick={increaseFontSize}>A+</Button>
          <Typography>{fontSize}px</Typography>
          <Button variant="contained" onClick={decreaseFontSize}>A-</Button>
          <select 
            onChange={(e) => changeFont(e.target.value)} 
            value={fontFamily}
            style={{ padding: '8px', marginLeft: '10px' }}
          >
            <option value="Arial">Arial</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
          </select>
        </Box>

        <Paper 
          elevation={3} 
          sx={{
            padding: "16px", 
            marginTop: "16px",
            backgroundColor: darkMode ? '#222' : '#fff',
            color: darkMode ? '#fff' : '#000',
          }}
        >
          <div
            style={{ 
              fontSize: `${fontSize}px`, 
              fontFamily: fontFamily,
              whiteSpace: "pre-line", 
              textAlign: "justify",
              lineHeight: '1.6'
            }}
          >
            {chapter.content}
          </div>
        </Paper>
      </Box>

      <Comments storyId={storyId} chapterId={chapterId} />

      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          variant="contained"
          href={`/stories/${storyId}/chapters/${parseInt(chapterId) - 1}`}
        >
          Chương trước
        </Button>
        <Button
          variant="contained"
          href={`/stories/${storyId}/chapters/${parseInt(chapterId) + 1}`}
        >
          Chương sau
        </Button>
      </Box>
    </Container>
  );
};

export default ChapterPage;